import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import EmployerCommerceShell from '../../components/employer/EmployerCommerceShell';
import { Button, Card, CardBody, CardHeader, Input } from '../../components/ui/primitives';
import {
  createCommerceOrder,
  fetchBillingProfile,
  fetchCreditBalance,
  fetchCreditPacks,
  type CommerceBillingProfile,
  type CommerceCheckoutMode,
  type CommerceCreditBalance,
  type CommerceOffering,
  useApi,
} from '../../services/api';
import { buildInvoiceDetailsUrl } from '../../services/commerceIntent';
import { rememberPendingCommerceOrder, submitPayFastHostedPayment } from '../../services/payfast';

const currencyFormatter = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  minimumFractionDigits: 2,
});

const formatCurrency = (value: string | number) => {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? currencyFormatter.format(amount) : 'R0.00';
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const getMetadataValue = (offering: CommerceOffering, key: string) => {
  const metadata = offering.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }
  return metadata[key] ?? null;
};

const getMetadataNumber = (offering: CommerceOffering, key: string) => {
  const value = getMetadataValue(offering, key);
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getMetadataString = (offering: CommerceOffering, key: string) => {
  const value = getMetadataValue(offering, key);
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const getDisplayOrder = (offering: CommerceOffering) => getMetadataNumber(offering, 'displayOrder') ?? 999;

const isPayAsYouGoOffering = (offering: CommerceOffering) =>
  getMetadataString(offering, 'pricingModel') === 'PAY_AS_YOU_GO';

const getPerCreditPrice = (offering: CommerceOffering) => {
  const metadataPrice =
    getMetadataString(offering, 'pricePerCreditZar') || getMetadataString(offering, 'unitPriceZar');
  if (metadataPrice) return metadataPrice;
  if (offering.credits && offering.credits > 0) {
    return (Number(offering.priceZar) / offering.credits).toFixed(2);
  }
  return offering.priceZar;
};

const getCreditSubtitle = (offering: CommerceOffering) =>
  getMetadataString(offering, 'displaySubtitle') ||
  (offering.credits ? `${offering.credits} credits` : 'Credits');

const EmployerBillingPage: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [creditPacks, setCreditPacks] = useState<CommerceOffering[]>([]);
  const [creditBalance, setCreditBalance] = useState<CommerceCreditBalance | null>(null);
  const [billingProfile, setBillingProfile] = useState<CommerceBillingProfile | null>(null);
  const [paygQuantity, setPaygQuantity] = useState('1');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setBillingProfile(null);

      try {
        const [offeringsResult, balanceResult, profileResult] = await Promise.allSettled([
          fetchCreditPacks(api),
          fetchCreditBalance(api),
          fetchBillingProfile(api),
        ]);

        if (!mounted) return;

        if (offeringsResult.status === 'fulfilled') {
          setCreditPacks(offeringsResult.value);
        } else {
          setCreditPacks([]);
          setLoadError(getErrorMessage(offeringsResult.reason, 'Unable to load credit options right now.'));
        }

        if (balanceResult.status === 'fulfilled') {
          setCreditBalance(balanceResult.value);
        } else {
          setCreditBalance(null);
          if (offeringsResult.status === 'fulfilled') {
            setLoadError('Unable to load credit balance right now. Credit options are still available.');
          }
        }

        if (profileResult.status === 'fulfilled') {
          setBillingProfile(profileResult.value);
        } else {
          setBillingProfile(null);
        }

        const offerings = offeringsResult.status === 'fulfilled' ? offeringsResult.value : [];
        const paygOffering = offerings.find((offering) => isPayAsYouGoOffering(offering));
        if (paygOffering) {
          setPaygQuantity(String(getMetadataNumber(paygOffering, 'minCredits') ?? 1));
        }
      } catch (error) {
        if (!mounted) return;
        setLoadError(getErrorMessage(error, 'Unable to load credit options right now.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [api]);

  const fixedPacks = useMemo(
    () =>
      [...creditPacks]
        .filter((offering) => !isPayAsYouGoOffering(offering))
        .sort((left, right) => getDisplayOrder(left) - getDisplayOrder(right)),
    [creditPacks]
  );
  const paygOffering = useMemo(
    () => creditPacks.find((offering) => isPayAsYouGoOffering(offering)) ?? null,
    [creditPacks]
  );
  const paygMin = paygOffering ? getMetadataNumber(paygOffering, 'minCredits') ?? 1 : 1;
  const paygMax = paygOffering ? getMetadataNumber(paygOffering, 'maxCredits') ?? 24 : 24;
  const paygUnitPrice = paygOffering ? Number(getPerCreditPrice(paygOffering)) : 0;
  const paygQuantityValue = Number(paygQuantity);
  const isValidPaygQuantity =
    Number.isInteger(paygQuantityValue) && paygQuantityValue >= paygMin && paygQuantityValue <= paygMax;
  const paygTotal = isValidPaygQuantity ? paygQuantityValue * paygUnitPrice : 0;
  const availableCreditsDisplay = loading
    ? ''
    : creditBalance
      ? String(creditBalance.availableBalance)
      : 'Unavailable';

  const startOrder = async (
    offering: CommerceOffering,
    checkoutMode: CommerceCheckoutMode,
    creditQuantity?: number
  ) => {
    const busyKey = `${offering.code}:${checkoutMode}:${creditQuantity ?? ''}`;
    setSubmittingKey(busyKey);

    try {
      if (checkoutMode === 'REQUEST_INVOICE' && !billingProfile) {
        navigate(
          buildInvoiceDetailsUrl({
            offeringCode: offering.code,
            flow: 'credits',
            creditQuantity,
          })
        );
        return;
      }

      const result = await createCommerceOrder(
        {
          offeringCode: offering.code,
          checkoutMode,
          creditQuantity,
        },
        api
      );

      if (checkoutMode === 'PAY_NOW') {
        if (!('payfastUrl' in result) || !result.payfastUrl || !result.formFields) {
          throw new Error('Hosted payment details were not returned.');
        }
        rememberPendingCommerceOrder(result.orderId);
        submitPayFastHostedPayment(result.payfastUrl, result.formFields);
        return;
      }

      toast.success('Invoice requested.');
      navigate(`/billing/order/${result.orderId}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to start this checkout.'));
    } finally {
      setSubmittingKey(null);
    }
  };

  return (
    <EmployerCommerceShell
      activeSection="billing"
      title="Top up credits"
      description="Choose a credit option and keep checkout simple. Pay now goes straight to payment. Invoice requests only ask for invoice details when needed."
      actions={
        <Button variant="secondary" onClick={() => navigate('/employer/build-request')}>
          Build request
        </Button>
      }
    >
      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <Card className="border-[#dbe4e1] bg-[linear-gradient(135deg,#ffffff_0%,#f0fdfa_100%)]">
          <CardHeader>
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Available credits</p>
          </CardHeader>
          <CardBody>
            <p className="min-h-[44px] font-mono text-[44px] font-bold leading-none text-[#0d9488]">
              {availableCreditsDisplay}
            </p>
            <p className="text-[14px] text-[#64748b]">Ready to use in your account now.</p>
          </CardBody>
        </Card>

        <Card className="border-[#dbe4e1]">
          <CardHeader>
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Invoice checkout</p>
          </CardHeader>
          <CardBody>
            <p className="text-[18px] font-semibold text-[#0f172a]">
              {billingProfile ? 'Saved invoice details available' : 'No invoice details saved yet'}
            </p>
            <p className="text-[14px] text-[#64748b]">
              {billingProfile
                ? 'Request invoice will use your saved details immediately.'
                : 'We will only ask for invoice details if you choose Request invoice.'}
            </p>
            <div className="pt-2">
              <Button variant="secondary" onClick={() => navigate('/employer/invoice-details')}>
                {billingProfile ? 'Edit invoice details' : 'Add invoice details'}
              </Button>
            </div>
          </CardBody>
        </Card>
      </section>

      {loadError ? (
        <Card className="border-[#fecaca] bg-[#fff5f5]">
          <p className="text-[14px] font-medium text-[#b91c1c]">{loadError}</p>
        </Card>
      ) : null}

      <Card className="border-[#dbe4e1]">
        <CardHeader>
          <h2 className="font-display text-[24px] font-bold text-[#0f172a]">Credit packs</h2>
          <p className="mt-1 text-[14px] text-[#64748b]">Fixed packs with clearer pricing for larger top-ups.</p>
        </CardHeader>
        <CardBody className="grid gap-4 lg:grid-cols-3">
          {loading ? <p className="text-[14px] text-[#64748b]">Loading credit packs...</p> : null}
          {!loading && fixedPacks.length === 0 ? (
            <div className="rounded-[14px] border border-dashed border-[#cbd5e1] bg-[#f8fafc] px-4 py-5 text-[14px] text-[#64748b]">
              No fixed credit packs are available right now.
            </div>
          ) : null}

          {fixedPacks.map((offering) => {
            const payNowKey = `${offering.code}:PAY_NOW:`;
            const invoiceKey = `${offering.code}:REQUEST_INVOICE:`;
            return (
              <Card key={offering.id} className="border-[#dbe4e1] bg-[#fcfffe]">
                <CardHeader>
                  <p className="text-[18px] font-semibold text-[#0f172a]">{offering.name}</p>
                  <p className="text-[13px] text-[#64748b]">{getCreditSubtitle(offering)}</p>
                </CardHeader>
                <CardBody>
                  <div>
                    <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Per credit</p>
                    <p className="mt-1 text-[28px] font-bold leading-none text-[#0d9488]">
                      {formatCurrency(getPerCreditPrice(offering))}
                    </p>
                  </div>
                  <div className="pt-1">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Total</p>
                    <p className="mt-1 text-[20px] font-semibold text-[#0f172a]">{formatCurrency(offering.priceZar)}</p>
                  </div>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <Button
                      onClick={() => startOrder(offering, 'PAY_NOW')}
                      disabled={submittingKey === payNowKey}
                    >
                      {submittingKey === payNowKey ? 'Redirecting...' : 'Pay now'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => startOrder(offering, 'REQUEST_INVOICE')}
                      disabled={submittingKey === invoiceKey}
                    >
                      {submittingKey === invoiceKey ? 'Starting...' : 'Request invoice'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </CardBody>
      </Card>

      <Card className="border-[#dbe4e1]">
        <CardHeader>
          <h2 className="font-display text-[24px] font-bold text-[#0f172a]">Pay as you go</h2>
          <p className="mt-1 text-[14px] text-[#64748b]">
            Need fewer than 25 credits? Choose the quantity you need and see the total before checkout.
          </p>
        </CardHeader>
        <CardBody>
          {paygOffering ? (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,0.55fr)_minmax(0,0.45fr)]">
              <div className="rounded-[16px] border border-[#dbe4e1] bg-[#fcfffe] p-5">
                <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Credits needed</p>
                <div className="mt-3 max-w-[180px]">
                  <Input
                    type="number"
                    min={paygMin}
                    max={paygMax}
                    value={paygQuantity}
                    onChange={(event) => setPaygQuantity(event.target.value)}
                  />
                </div>
                <p className="mt-2 text-[13px] text-[#64748b]">
                  Choose between {paygMin} and {paygMax} credits.
                </p>
              </div>

              <div className="rounded-[16px] border border-[#dbe4e1] bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] p-5">
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Per credit</p>
                  <p className="mt-1 text-[28px] font-bold leading-none text-[#0d9488]">
                    {formatCurrency(paygUnitPrice)}
                  </p>
                </div>
                <div className="pt-4">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Total</p>
                  <p className="mt-1 text-[22px] font-semibold text-[#0f172a]">
                    {isValidPaygQuantity ? formatCurrency(paygTotal) : 'Choose a valid quantity'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 pt-5">
                  <Button
                    onClick={() => startOrder(paygOffering, 'PAY_NOW', paygQuantityValue)}
                    disabled={!isValidPaygQuantity || submittingKey === `${paygOffering.code}:PAY_NOW:${paygQuantityValue}`}
                  >
                    {submittingKey === `${paygOffering.code}:PAY_NOW:${paygQuantityValue}` ? 'Redirecting...' : 'Pay now'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => startOrder(paygOffering, 'REQUEST_INVOICE', paygQuantityValue)}
                    disabled={
                      !isValidPaygQuantity ||
                      submittingKey === `${paygOffering.code}:REQUEST_INVOICE:${paygQuantityValue}`
                    }
                  >
                    {submittingKey === `${paygOffering.code}:REQUEST_INVOICE:${paygQuantityValue}`
                      ? 'Starting...'
                      : 'Request invoice'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[14px] border border-dashed border-[#cbd5e1] bg-[#f8fafc] px-4 py-5 text-[14px] text-[#64748b]">
              Pay-as-you-go is not available right now.
            </div>
          )}
        </CardBody>
      </Card>
    </EmployerCommerceShell>
  );
};

export default EmployerBillingPage;
